import React, { useState, useEffect, useCallback, useContext } from 'react';
import { faEdit, faFileInvoiceDollar, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { getAccountTypes, getAccounts, getAccountType } from '../business-accounts/BusinessAccountService';
import { Alert, Table, Card, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { extractError } from 'api/ErrorUtil';
import { NewButton } from 'uiwidgets/NewButton';
import _ from "lodash";
import { PAGE_SIZE } from '../../config/config';
import { TenantContext } from '../../tenant/TenantContext';
import { containsAlphaNumAndSpaceAndPunctuationOnly } from 'util/Validators';
import { FormContainer } from 'uiwidgets/FormContainer';
import { FormField } from 'uiwidgets/FormField';

function BusinessAccounts() {

  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [infoMessage, setInfoMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalNumberOfPages, setTotalNumberOfPages] = useState(0);
  const { businessConfig } = useContext(TenantContext);
  const [keyword, setKeyword] = useState("");
  const [keywordValidityData, setKeywordValidityData] = useState({ keywordErrMsg: "", keywordError: false })

  const onToastClose = () => {
    setErrors({});
    setInfoMessage("");
  }

  const fetchAccountsAndTypesForce = useCallback(async () => {
    try {
      let acctsPage = await getAccounts(currentPage, PAGE_SIZE);
      setKeyword("");
      setAccounts(acctsPage.accounts);
      setTotalNumberOfPages(acctsPage.totalNumberOfPages)
      setCurrentPage(0);
    } catch (error) {
      let { message, errors, httpStatus } = extractError(error);
      if (httpStatus === 404) {
        setInfoMessage("No accounts were found");
        setAccounts([]);
      } else {
        setErrors({ message, errors, httpStatus });
      }
    }
  });
  const fetchAccountsAndTypes = useCallback(async () => {
    try {
      let acctsPage = await getAccounts(currentPage, PAGE_SIZE, keyword);
      let actypes = null;
      if (_.isEmpty(accountTypes)) {
        actypes = await getAccountTypes();
        setAccountTypes(actypes);
      }
      setAccounts(acctsPage.accounts);
      setTotalNumberOfPages(acctsPage.totalNumberOfPages)
    } catch (error) {
      let { message, errors, httpStatus } = extractError(error);
      if (httpStatus === 404) {
        setInfoMessage("No accounts were found");
        setAccounts([]);
      } else {
        setErrors({ message, errors, httpStatus });
      }
    }
  });


  useEffect(() => {
    fetchAccountsAndTypes();
  }, [currentPage])

  useEffect(() => {
    if (keyword.length > 3) {
      console.log("Fetching accounts filter grater than 3")
      fetchAccountsAndTypes();
    }
  }, [keyword]);

  const createNewAccout = async (event) => {
    event.preventDefault();
    navigate('/businessaccount');
  }

  const editButtonClick = async (event, linkText) => {
    navigate(linkText);
  }

  const handlePaging = async (page) => {
    if (page < 0) {
      alert("Reached the beginning of data set")
      page = 0;
    }
    if (page > totalNumberOfPages - 1) {
      alert("Reached the end of data set")
      page = totalNumberOfPages - 1;
    }
    setCurrentPage(page);
  }

  const handleFilterKeyDown = async (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      fetchAccountsAndTypes();
    }
  };
  const clearFilter = async (event) => {
    fetchAccountsAndTypesForce();
  };
  const handleKeywordChange = async (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setKeyword(value);
  }

  const handleValidation = async (event) => {
    const name = event.target.name;
    const value = event.target.value;
    let { hasError, error } = validateInput(name, value);
    setKeywordValidityData({
      ['keywordError']: hasError,
      ['keywordErrMsg']: error
    })
  }

  const validateInput = (name, value) => {
    let hasError = false,
      error = ""
    switch (name) {
      case "keyword":
        if (value.trim() === "") {
          hasError = false
          error = ""
        } else if (!containsAlphaNumAndSpaceAndPunctuationOnly(value)) {
          hasError = true
          error = "Invalid keyword. Only alphanumeric characters are allowed";
        } else {
          hasError = false
          error = ""
        }
        break
      default:
        break;
    }
    return { hasError, error };
  }

  return (
    <>
      <FormContainer title="Accounts" toastCloseFunc={onToastClose} errors={errors} infoMessage={infoMessage} >
        <Row>
          <Col sm={12} md={4} lg={4}>
            <Button label='New Account' onClick={createNewAccout}
              className={'btn-fill pull-right '} size='btn-sm' variant="info">New Account</Button>
            &nbsp;<Button label='Import Accounts' onClick={event => { navigate('/businessaccounts/import'); }}
              className={'btn-fill pull-right'} size='btn-sm' variant="info">Import Accounts</Button>
          </Col>
          <Col sm={12} md={4} lg={4}>&nbsp;</Col>
        </Row>
        <Row>
          <Col sm={12} md={4} lg={4}>
            <FormField name="keyword" label="Keyword" type="text" value={keyword}
              placeholder="email, account number" onChange={handleKeywordChange}
              onBlur={handleValidation} onKeyDown={handleFilterKeyDown}
              valstate={!keywordValidityData.keywordError} valMsg={keywordValidityData.keywordErrMsg} />
          </Col>

          <Col sm={6} md={2} lg={2}>
            <br />
            <Button variant="info" className="border-0 btn-fill" onClick={clearFilter}><strong><small>Clear Filter</small></strong></Button>
          </Col>
        </Row>


        <Row>
          <Col className='d-flex justify-content-end'>
            <Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage - 1) }}>
              <h6><FontAwesomeIcon icon={faArrowLeft} onClick={(event) => { handlePaging(currentPage - 1) }} />Previous Page</h6>
            </Button>
            &nbsp;<span className="border-0 text-dark btn"><h6>{currentPage + 1} of {totalNumberOfPages}</h6></span>
            &nbsp;<Button variant="dark" className="border-0 text-dark" onClick={(event) => { handlePaging(currentPage + 1) }}>
              <h6>Next Page<FontAwesomeIcon icon={faArrowRight} /></h6>
            </Button>
          </Col>

        </Row>


        <Table className="table-hover table-striped">
          <thead>
            <tr>
              <th className="border-0">Number</th>
              <th className="border-0">Account Owner</th>
              <th className="border-0">Type</th>
              <th className="border-0">Address</th>
              <th className="border-0">Email</th>
              <th className="border-0">City</th>
              <th className="border-0">Province</th>
              <th className="border-0">Postal Code</th>
              <th className="border-0">Country</th>
              <th className="border-0">Edit</th>
              {businessConfig.appFeatures.includes('BILLING') && <th className="border-0">New Bill</th>}
            </tr>
          </thead>
          <tbody>
            {!_.isEmpty(accounts) && !_.isEmpty(accountTypes) &&
              accounts.map((acc, idx) => {

                let accountType = getAccountType(acc.account.accountTypeId, accountTypes);

                return (
                  <React.Fragment key={idx}>
                    <tr key={idx}>
                      <td>
                        {acc.account.number}
                      </td>
                      <td>
                        {_.isEmpty(acc.account.companyName) ? 
                          <>{acc.account.firstName} {acc.account.lastName}</>
                          :
                          <>{acc.account.companyName}({acc.account.firstName} {acc.account.lastName})</>
                          }
                        
                      </td>
                      <td>
                        {accountType.displayName}
                      </td>
                      <td>
                        {acc.account.address}
                      </td>
                      <td>
                        {acc.account.email}
                      </td>
                      <td>
                        {acc.account.city}
                      </td>
                      <td>
                        {acc.account.state}
                      </td>

                      <td>
                        {acc.account.zip}
                      </td>

                      <td>
                        {acc.account.country}
                      </td>
                      <td>
                        <Button title="Edit Account" variant="dark" className="border-0 text-dark" onClick={(event) => editButtonClick(event, "/businessaccounts/" + acc.account.id)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </td>
                      <td>
                        {businessConfig.appFeatures.includes('BILLING') && accountType.type != 'DONATION' &&
                          <Button title="Create New Bill" variant="dark" className="border-0 text-dark" onClick={(event) => editButtonClick(event, "/accountbusinessbill/" + acc.account.id)}>
                            <FontAwesomeIcon icon={faFileInvoiceDollar} />
                          </Button>
                        }</td>


                    </tr>
                  </React.Fragment>
                )
              })}
          </tbody>
        </Table>
      </FormContainer>
    </>
  );
}

export default BusinessAccounts;